import { CLOUD_PROVIDER_DISPLAY, type CloudProvider } from "../data/types";
import type { LlmEngine, LlmRequest, LlmResult } from "./types";

const ANTHROPIC_MODEL = "claude-3-5-sonnet-latest";
const OPENAI_MODEL = "gpt-4o-mini";
const GEMINI_MODEL = "gemini-1.5-flash-latest";

export function createCloudEngine(
  provider: CloudProvider,
  apiKey: string,
): LlmEngine {
  const displayName = `Cloud · ${CLOUD_PROVIDER_DISPLAY[provider]}`;
  return {
    displayName,
    async complete(request: LlmRequest): Promise<LlmResult> {
      if (apiKey.trim().length === 0) {
        return {
          kind: "error",
          message: `No API key set for ${CLOUD_PROVIDER_DISPLAY[provider]}.`,
          source: displayName,
        };
      }
      try {
        const text = await routeRequest(provider, apiKey, request);
        const cleaned = text.trim();
        if (cleaned.length === 0) {
          return { kind: "error", message: "Empty response from provider.", source: displayName };
        }
        return { kind: "success", text: cleaned, source: displayName };
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        return { kind: "error", message, source: displayName };
      }
    },
  };
}

async function routeRequest(
  provider: CloudProvider,
  apiKey: string,
  request: LlmRequest,
): Promise<string> {
  switch (provider) {
    case "Anthropic":
      return callAnthropic(apiKey, request);
    case "OpenAI":
      return callOpenAi(apiKey, request);
    case "Gemini":
      return callGemini(apiKey, request);
  }
}

async function callAnthropic(apiKey: string, request: LlmRequest): Promise<string> {
  const system = request.messages
    .filter((m) => m.role === "system")
    .map((m) => m.content)
    .join("\n\n");
  const conversation = request.messages.filter((m) => m.role !== "system");
  const body: Record<string, unknown> = {
    model: ANTHROPIC_MODEL,
    max_tokens: request.maxTokens ?? 512,
    temperature: request.temperature ?? 0.3,
    messages: conversation.map((m) => ({
      role: m.role === "assistant" ? "assistant" : "user",
      content: m.content,
    })),
  };
  if (system.length > 0) body.system = system;

  const res = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": apiKey,
      "anthropic-version": "2023-06-01",
      // Required for browser-origin requests so Anthropic doesn't block CORS.
      "anthropic-dangerous-direct-browser-access": "true",
    },
    body: JSON.stringify(body),
  });
  const json = (await safeJson(res)) as {
    content?: Array<{ type?: string; text?: string }>;
    error?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(json?.error?.message ?? `${res.status} from Anthropic`);
  }
  const text = json.content?.find((c) => c.type === "text")?.text;
  if (!text) throw new Error("No text content in Anthropic response.");
  return text;
}

async function callOpenAi(apiKey: string, request: LlmRequest): Promise<string> {
  const body = {
    model: OPENAI_MODEL,
    max_tokens: request.maxTokens ?? 512,
    temperature: request.temperature ?? 0.3,
    messages: request.messages.map((m) => ({ role: m.role, content: m.content })),
  };
  const res = await fetch("https://api.openai.com/v1/chat/completions", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify(body),
  });
  const json = (await safeJson(res)) as {
    choices?: Array<{ message?: { content?: string } }>;
    error?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(json?.error?.message ?? `${res.status} from OpenAI`);
  }
  const text = json.choices?.[0]?.message?.content;
  if (!text) throw new Error("No content in OpenAI response.");
  return text;
}

async function callGemini(apiKey: string, request: LlmRequest): Promise<string> {
  const systemText = request.messages
    .filter((m) => m.role === "system")
    .map((m) => m.content)
    .join("\n\n");
  const conversation = request.messages.filter((m) => m.role !== "system");
  const body: Record<string, unknown> = {
    contents: conversation.map((m) => ({
      role: m.role === "assistant" ? "model" : "user",
      parts: [{ text: m.content }],
    })),
    generationConfig: {
      temperature: request.temperature ?? 0.3,
      maxOutputTokens: request.maxTokens ?? 512,
    },
  };
  if (systemText.length > 0) {
    body.systemInstruction = { parts: [{ text: systemText }] };
  }

  const url = `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent?key=${encodeURIComponent(apiKey)}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = (await safeJson(res)) as {
    candidates?: Array<{ content?: { parts?: Array<{ text?: string }> } }>;
    error?: { message?: string };
  };
  if (!res.ok) {
    throw new Error(json?.error?.message ?? `${res.status} from Gemini`);
  }
  const text = json.candidates?.[0]?.content?.parts?.[0]?.text;
  if (!text) throw new Error("No text in Gemini response.");
  return text;
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return {};
  }
}
